import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Card,
  CardContent,
  Grid,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Alert,
  Toolbar,
  InputAdornment,
  CircularProgress,
  Snackbar,
  Tooltip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Email as EmailIcon,
  Code as CodeIcon,
  Link as LinkIcon,
  CheckCircle as ConsumedIcon,
  HourglassEmpty as PendingIcon,
  DeleteSweep as CleanupIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  fetchEmails,
  fetchStats,
  consumeEmail,
  cleanupExpired,
} from '../redux/slices/emailInterceptSlice';

const STATUS_CONFIG = {
  received: { label: 'Received', color: 'default' },
  code_extracted: { label: 'Code Extracted', color: 'info' },
  link_extracted: { label: 'Link Extracted', color: 'info' },
  consumed: { label: 'Consumed', color: 'success' },
  expired: { label: 'Expired', color: 'error' },
};

function StatCard({ title, value, icon, color }) {
  return (
    <Card sx={{ minWidth: 140 }}>
      <CardContent sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5, '&:last-child': { pb: 1.5 } }}>
        <Box sx={{ color: color || 'primary.main' }}>{icon}</Box>
        <Box>
          <Typography variant="h5" fontWeight="bold">
            {value ?? '--'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {title}
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function EmailInterceptPage() {
  const dispatch = useDispatch();
  const { emails, stats, loading, error } = useSelector((state) => state.emailIntercept);
  const user = useSelector((state) => state.auth.user);
  const userRole = user?.role;
  const isAdmin = userRole === 'ADMIN' || userRole === 'SUPER_ADMIN';

  const [searchEmail, setSearchEmail] = useState('');
  const [filterAppKey, setFilterAppKey] = useState('');
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const intervalRef = useRef(null);

  const loadData = useCallback(() => {
    dispatch(fetchEmails());
    dispatch(fetchStats());
  }, [dispatch]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh polling
  useEffect(() => {
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        dispatch(fetchEmails());
        dispatch(fetchStats());
      }, 5000);
    } else {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, dispatch]);

  const showMessage = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleConsume = useCallback(async (id) => {
    try {
      await dispatch(consumeEmail(id));
      showMessage('Email marked as consumed');
    } catch (err) {
      showMessage('Failed to consume email', 'error');
    }
  }, [dispatch, showMessage]);

  const handleCleanup = useCallback(async () => {
    try {
      const result = await dispatch(cleanupExpired()).unwrap();
      const count = result?.deleted ?? 0;
      showMessage(`Cleaned up ${count} expired email(s)`);
    } catch (err) {
      showMessage('Failed to clean up expired emails', 'error');
    }
  }, [dispatch, showMessage]);

  // Derive unique app keys for filter dropdown
  const appKeys = [...new Set(
    (Array.isArray(emails) ? emails : [])
      .map((e) => e.appKey)
      .filter(Boolean)
  )].sort();

  // Filter emails
  const filteredEmails = (Array.isArray(emails) ? emails : []).filter((email) => {
    const matchesSearch =
      !searchEmail ||
      email.recipient?.toLowerCase().includes(searchEmail.toLowerCase());
    const matchesApp = !filterAppKey || email.appKey === filterAppKey;
    return matchesSearch && matchesApp;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '--';
    const d = new Date(dateStr);
    return d.toLocaleString();
  };

  if (!isAdmin) {
    return (
      <Box p={3}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Access Denied: The Email Intercept feature is only available to administrators.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Email Interception</Typography>
        <Box display="flex" alignItems="center" gap={1}>
          <FormControlLabel
            control={
              <Switch
                checked={autoRefresh}
                onChange={(e) => setAutoRefresh(e.target.checked)}
                size="small"
              />
            }
            label="Auto-refresh"
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadData}
            size="small"
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<CleanupIcon />}
            onClick={handleCleanup}
            size="small"
          >
            Delete Expired
          </Button>
        </Box>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item>
          <StatCard
            title="Total Received"
            value={stats.totalReceived}
            icon={<EmailIcon />}
            color="primary.main"
          />
        </Grid>
        <Grid item>
          <StatCard
            title="Codes Extracted"
            value={stats.codesExtracted}
            icon={<CodeIcon />}
            color="info.main"
          />
        </Grid>
        <Grid item>
          <StatCard
            title="Links Extracted"
            value={stats.linksExtracted}
            icon={<LinkIcon />}
            color="info.main"
          />
        </Grid>
        <Grid item>
          <StatCard
            title="Consumed"
            value={stats.consumed}
            icon={<ConsumedIcon />}
            color="success.main"
          />
        </Grid>
        <Grid item>
          <StatCard
            title="Pending"
            value={stats.pending}
            icon={<PendingIcon />}
            color="warning.main"
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Toolbar disableGutters sx={{ gap: 2, mb: 2 }}>
        <TextField
          size="small"
          placeholder="Search by recipient email..."
          value={searchEmail}
          onChange={(e) => setSearchEmail(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
          sx={{ width: 300 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>App Key</InputLabel>
          <Select
            value={filterAppKey}
            label="App Key"
            onChange={(e) => setFilterAppKey(e.target.value)}
          >
            <MenuItem value="">All Apps</MenuItem>
            {appKeys.map((key) => (
              <MenuItem key={key} value={key}>{key}</MenuItem>
            ))}
          </Select>
        </FormControl>
        {autoRefresh && (
          <Chip
            label="Auto-refreshing every 5s"
            color="primary"
            size="small"
            variant="outlined"
          />
        )}
      </Toolbar>

      {/* Email Table */}
      {loading && !emails.length ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Timestamp</TableCell>
                <TableCell>Recipient</TableCell>
                <TableCell>Sender</TableCell>
                <TableCell>App</TableCell>
                <TableCell>Subject</TableCell>
                <TableCell>Extracted Code/Link</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEmails.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No intercepted emails found.
                  </TableCell>
                </TableRow>
              ) : (
                filteredEmails.map((email) => {
                  const statusCfg = STATUS_CONFIG[email.status] || STATUS_CONFIG.received;
                  return (
                    <TableRow key={email.id}>
                      <TableCell>
                        <Typography variant="body2" noWrap>
                          {formatDate(email.receivedAt || email.createdAt)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                          {email.recipient || '--'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" noWrap sx={{ maxWidth: 180 }}>
                          {email.sender || '--'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {email.appKey ? (
                          <Chip label={email.appKey} size="small" variant="outlined" />
                        ) : (
                          <Typography variant="body2" color="text.secondary">--</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Tooltip title={email.subject || ''}>
                          <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                            {email.subject || '--'}
                          </Typography>
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        {email.extractedCode ? (
                          <Chip
                            label={email.extractedCode}
                            size="small"
                            color="info"
                            icon={<CodeIcon />}
                          />
                        ) : email.extractedLink ? (
                          <Tooltip title={email.extractedLink}>
                            <Chip
                              label="Link"
                              size="small"
                              color="info"
                              icon={<LinkIcon />}
                              clickable
                              component="a"
                              href={email.extractedLink}
                              target="_blank"
                              rel="noopener noreferrer"
                            />
                          </Tooltip>
                        ) : (
                          <Typography variant="body2" color="text.secondary">--</Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={statusCfg.label}
                          color={statusCfg.color}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="right">
                        {email.status !== 'consumed' && email.status !== 'expired' && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleConsume(email.id)}
                          >
                            Mark Consumed
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
